import { Image, Tooltip, Typography } from 'antd';
import {
  PaperClipOutlined,
  FilePdfOutlined,
  FileOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import { Attachment } from '@/types';
import { useTranslation } from 'react-i18next';
import { THEME_VARIABLE } from '@/config';

interface Props {
  file: Attachment;
  onDelete?: (id: number | string) => void;
}

const getFileIcon = (type?: string) => {
  if (!type) return <FileOutlined style={{ fontSize: 28, color: '#8c8c8c' }} />;
  if (type.includes('pdf')) return <FilePdfOutlined style={{ fontSize: 28, color: '#cf1322' }} />;
  if (type.startsWith('video/')) return <FileOutlined style={{ fontSize: 28, color: '#fa8c16' }} />;
  return <PaperClipOutlined style={{ fontSize: 28, color: '#8c8c8c' }} />;
};

const AttachmentItem = ({ file, onDelete }: Props) => {
  const { t } = useTranslation();

  const isImage = file.content_type?.startsWith('image/');

  const renderFileName = (name: string) => {
    if (!name) return 'attachment';
    const dotIndex = name.lastIndexOf('.');
    const base = dotIndex > -1 ? name.substring(0, dotIndex) : name;
    const ext = dotIndex > -1 ? name.substring(dotIndex) : '';

    const shortBase = base.length > 12 ? base.substring(0, 6) + '...' : base;

    return shortBase + ext;
  };

  const handleClick = () => {
    if (!isImage) {
      window.open(file.mapped_content_url, '_blank');
    }
  };

  return (
    <div
      key={file.id}
      style={{
        width: 80,
        height: 80,
        borderRadius: 8,
        border: '1px solid #f0f0f0',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
      }}
      className='bg-slate-50 relative'
      onClick={handleClick}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px 8px 0 0 ',
          overflow: 'hidden',
        }}
      >
        {isImage ? (
          <Image
            src={file.mapped_content_url}
            width={80}
            height={60}
            style={{ objectFit: 'cover' }}
            preview={{
              src: file.mapped_content_url,
              mask: <span>{t('view')}</span>,
            }}
          />
        ) : (
          getFileIcon(file.content_type)
        )}
      </div>
      {onDelete && (
        <CloseCircleFilled
          className='hover:opacity-100 hover:text-gray-400 text-gray-300 bg-slate-500'
          onClick={(e) => {
            e.stopPropagation();
            onDelete(file.id);
          }}
          style={{
            position: 'absolute',
            top: -5,
            right: -5,
            fontSize: 16,
            cursor: 'pointer',
            background: '#fff',
            borderRadius: '50%',
          }}
        />
      )}
      <Tooltip title={file.file_name}>
        <Typography.Text
          style={{
            display: 'block',
            padding: '0 4px',
            textAlign: 'center',
            fontSize: 10,
            maxWidth: 72,
          }}
        >
          {renderFileName(file.file_name || t('attachment'))}
        </Typography.Text>
      </Tooltip>
    </div>
  );
};

export default AttachmentItem;
